import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { indicatorEditStore } from '@stores/dictionaryStores/indicatorsStore/indicatorEditStore';
import { indicatorsStore } from '@stores/dictionaryStores/indicatorsStore/indicatorsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { Form } from './Form';
import { RulesTable } from './RulesTable';
import { validate } from './validateSchema';

import styles from './IndicatorEdit.module.scss';

export const IndicatorEdit = observer(() => {
  const { varCode } = useParams<{ varCode: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, indicator, save, isNew, isEditMode, getVarSources, getAggFuncs, clear } =
    indicatorEditStore;
  const { selectNode } = indicatorsStore;
  const { reportVersions } = reportVersionsStore;

  const permission = usePermission('CmIndicator', indicatorEditStore);

  useEffect(() => {
    if (varCode !== NEW_ENTITY_ID) {
      fetch(varCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, varCode]);

  useEffect(() => {
    getVarSources();
    getAggFuncs();

    return () => clear();
  }, [clear, getAggFuncs, getVarSources]);

  const initialValues = useMemo(
    () =>
      indicator
        ? {
            ...indicator,
            sourceField:
              indicator.varSource === 'reg_meta'
                ? `${indicator.techSourceTable}.${indicator.sourceField}`
                : indicator.sourceField,
            reportGroupCode: reportVersions?.find((version) => version.code === indicator?.repVers)?.reportGroupCode,
            decimalQty: indicator.decimalQty ? String(indicator.decimalQty) : undefined,
          }
        : {
            reportGroupCode: reportVersions?.find((version) => version.code === selectNode?.repVers)?.reportGroupCode,
            repVers: selectNode?.repVers,
          },
    [indicator, reportVersions, selectNode?.repVers],
  );

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save({
          ...values,
          sourceField: values.varSource === 'reg_meta' ? values.sourceField.split('.')[1] : values.sourceField,
          techSourceTable: values.varSource === 'reg_meta' ? values.sourceField.split('.')[0] : undefined,
          filters: values.filters?.map((item) => ({
            ...item,
            varCode: values.varCode,
            fieldName: item.fieldName.split('.')[1],
          })),
        });

        if (isNew && value?.varCode) {
          push(`${RoutePath.DICTIONARY_INDICATORS}/${value.varCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  const goToIndicators = useCallback(() => push(RoutePath.DICTIONARY_INDICATORS), [push]);

  return (
    <DesignLayout title="Справочник: Показатели" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper
        className={styles.form}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        layout="vertical"
        compact
      >
        <FormWithActions store={indicatorEditStore} goBackLink={goToIndicators}>
          <Form />
          <RulesTable />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
});
